/**
 * Client theme resolver for white-label OAuth flows.
 *
 * Uses @theme-token/sdk to fetch themes from the blockchain.
 * The SDK's ThemeToken.styles contains light/dark mode CSS variable
 * sets that are directly compatible with shadcn/ui.
 *
 * Caches results in-memory with a 5-minute TTL.
 */

import type { ThemeStyleProps, ThemeToken } from "@theme-token/sdk";
import { fetchThemeByOrigin } from "@theme-token/sdk";
import { findOAuthClientThemeOrigin } from "@/lib/data/oauth-client-repository";

export interface ClientTheme {
	/** The full ThemeToken from the blockchain */
	token: ThemeToken;
	/** The theme-token ordinal origin */
	themeOrigin: string;
}

interface CacheEntry {
	theme: ClientTheme | null;
	expiresAt: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000;
const themeCache = new Map<string, CacheEntry>();

const CSS_KEY_MAP: Record<string, string> = {
	"letter-spacing": "tracking-normal",
	"shadow-offset-x": "shadow-x",
	"shadow-offset-y": "shadow-y",
};

/**
 * Convert ThemeStyleProps to CSS custom properties for React style prop.
 * Uses the same key mapping as the SDK's applyTheme().
 */
export function toCssVars(styles: ThemeStyleProps): Record<string, string> {
	const vars: Record<string, string> = {};
	for (const [key, value] of Object.entries(styles)) {
		if (typeof value !== "string") {
			continue;
		}
		vars[`--${CSS_KEY_MAP[key] ?? key}`] = value;
	}
	return vars;
}

/**
 * Resolve the theme for a given OAuth client ID.
 *
 * Returns null when the client doesn't exist or has no themeOrigin set.
 */
export async function resolveClientTheme(
	clientId: string
): Promise<ClientTheme | null> {
	const now = Date.now();
	const cached = themeCache.get(clientId);

	if (cached && cached.expiresAt > now) {
		return cached.theme;
	}

	const themeOrigin = await findOAuthClientThemeOrigin(clientId);

	if (!themeOrigin) {
		themeCache.set(clientId, { theme: null, expiresAt: now + CACHE_TTL_MS });
		return null;
	}

	const published = await fetchThemeByOrigin(themeOrigin);
	if (!published) {
		themeCache.set(clientId, { theme: null, expiresAt: now + CACHE_TTL_MS });
		return null;
	}

	const theme: ClientTheme = {
		token: published.theme,
		themeOrigin,
	};

	themeCache.set(clientId, { theme, expiresAt: now + CACHE_TTL_MS });
	return theme;
}
